<template>
  <Base>
    <template #toolbar>
      <div class="toolbar" id="kt_toolbar">
        <div class="container-fluid d-flex flex-stack" id="kt_toolbar_container">
          <div class="d-flex align-items-center me-3">
            <span class="h-20px border-gray-200 border-start mx-4"></span>
            <ul class="breadcrumb breadcrumb-separatorless fw-bold fs-7 my-1">
              <li class="breadcrumb-item text-muted">
                <router-link :to="{ name: 'dashboard' }" class="text-muted text-hover-primary">
                  <i class="fad fa-home"></i> Tableau de bord
                </router-link>
              </li>
              <li class="breadcrumb-item"><span class="bullet bg-gray-200 w-5px h-2px"></span></li>
              <li class="breadcrumb-item text-dark">Comptes Retell AI</li>
            </ul>
          </div>
          <div class="d-flex align-items-center py-1">
            <button class="btn btn-primary btn-sm" @click="resetForm" v-b-modal="'accountModal'">
              <i class="fad fa-plus me-1"></i> Nouveau compte
            </button>
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <div class="card">
        <div class="card-body">
          <div v-if="accounts.length === 0" class="text-center text-muted py-10">
            <i class="fad fa-phone-volume fs-3x mb-3 d-block"></i>
            Aucun compte Retell AI configure. Ajoutez-en un pour activer les appels vocaux IA.
          </div>
          <div class="table-responsive" v-else>
            <table class="table table-striped align-middle">
              <thead>
                <tr class="fw-bolder text-muted">
                  <th>Nom</th>
                  <th>Agent ID</th>
                  <th>Numero sortant</th>
                  <th class="text-center">Par defaut</th>
                  <th class="text-center">Actif</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in accounts" :key="a.id">
                  <td class="fw-bolder">{{ a.label }}</td>
                  <td class="text-muted fs-8">{{ a.agentId }}</td>
                  <td class="text-muted">{{ a.phoneNumberId || '-' }}</td>
                  <td class="text-center">
                    <i v-if="a.isDefault" class="fad fa-star text-warning"></i>
                  </td>
                  <td class="text-center">
                    <span v-if="a.isActive" class="badge badge-light-success">Actif</span>
                    <span v-else class="badge badge-light-danger">Inactif</span>
                  </td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-light-info me-1" @click="testAccount(a)">
                      <i class="fad fa-plug"></i>
                    </button>
                    <button class="btn btn-sm btn-light-warning me-1" @click="editAccount(a)" v-b-modal="'accountModal'">
                      <i class="fad fa-edit"></i>
                    </button>
                    <button class="btn btn-sm btn-light-danger" @click="deleteAccount(a)">
                      <i class="fad fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Modal Create/Edit -->
      <b-modal id="accountModal" :title="form.id ? 'Modifier le compte' : 'Nouveau compte'" centered size="lg" @ok.prevent="saveAccount">
        <div class="row">
          <div class="col-12 mb-3">
            <label class="required form-label">Nom</label>
            <b-form-input v-model="form.label" placeholder="Ex: Compte principal, Relance renouvellement"></b-form-input>
          </div>
          <div class="col-12 mb-3">
            <label class="required form-label">Cle API</label>
            <b-form-input v-model="form.apiKey" type="password" placeholder="Cle API Retell AI"></b-form-input>
          </div>
          <div class="col-6 mb-3">
            <label class="required form-label">Agent ID</label>
            <b-form-input v-model="form.agentId" placeholder="Ex: agent_abc123"></b-form-input>
            <div class="text-muted fs-8">ID de l'agent Retell AI</div>
          </div>
          <div class="col-6 mb-3">
            <label class="form-label">Numero de telephone</label>
            <b-form-input v-model="form.phoneNumberId" placeholder="Ex: +2250789986979"></b-form-input>
            <div class="text-muted fs-8">Numero de telephone Retell pour les appels sortants</div>
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">Webhook URL</label>
            <b-form-input v-model="form.webhookUrl" placeholder="https://..."></b-form-input>
            <div class="text-muted fs-8">URL webhook pour le traitement des conversations</div>
          </div>
          <div class="col-6 mb-3">
            <b-form-checkbox v-model="form.isActive">Actif</b-form-checkbox>
          </div>
          <div class="col-6 mb-3">
            <b-form-checkbox v-model="form.isDefault">Compte par defaut</b-form-checkbox>
          </div>
        </div>
      </b-modal>
    </template>
  </Base>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-property-decorator';
import Base from "@/layouts/Base.vue";
import {api} from "@/services/Api";

@Component({components: {Base}})
export default class RetellAccountManager extends Vue {
  accounts: any[] = []
  form: any = {}

  mounted() {
    this.loadAccounts()
  }

  resetForm() {
    this.form = {
      label: '', apiKey: '', agentId: '',
      phoneNumberId: '', webhookUrl: '',
      isActive: true, isDefault: false
    }
  }

  editAccount(a: any) {
    this.form = {...a}
  }

  async loadAccounts() {
    this.$store.commit('loading')
    const res = await api.get(api.com, 'retell/account/list')
    this.$store.commit('stopLoading')
    if (res?.data?.accounts) {
      this.accounts = res.data.accounts
    }
  }

  async saveAccount() {
    this.$store.commit('loading')
    const res = await api.post(api.com, 'retell/account/edit', this.form)
    this.$store.commit('stopLoading')
    if (res?.data?.status === 'success') {
      this.$bvModal?.hide?.('accountModal')
      await this.loadAccounts()
    }
  }

  async deleteAccount(a: any) {
    if (!confirm('Supprimer ce compte Retell AI ?')) return
    await api.get(api.com, 'retell/account/delete/' + a.id)
    await this.loadAccounts()
  }

  async testAccount(a: any) {
    this.$store.commit('loading')
    const res = await api.post(api.com, 'retell/account/test/' + a.id, {})
    this.$store.commit('stopLoading')
    if (res?.data?.status === 'success') {
      alert(res.data.message || 'Connexion reussie')
    } else {
      alert(res?.data?.message || 'Echec de la connexion')
    }
  }
}
</script>
